import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { FormikProps, Formik, Form, Field } from "formik";
import { Col, Row } from "react-bootstrap";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { get, map, pickBy, isEmpty, find, uniq, sortBy, indexOf, includes } from "lodash";
import { InputCheckboxField } from "../../../../../components/Shared/InputCheckboxField";
import * as XLSX from "xlsx";
import qs from "qs";
import { request } from "../../../../../utils/Request";
import { utcDateToLocalDate, customSplitter, groupIdsByValue } from "../../../../../utils/Helper";
import { getLocation } from "../../../../../components/Common/Display/Location";

interface IFormProps {
    t?: any | undefined;
    moduleState: any;
    setQuery: any;
    dropdowns: any;
    visibility: any;
    toastify: any;
    onClose?: any;
}

type TInputForm = {
    consider_all_pages: boolean,
    load_number: boolean,
    driver_name: boolean,
    cmr: boolean,
    legal_entity: boolean,
    ldg_nr: boolean,
    client: boolean,
    brand_model: boolean,
    transport_vehicle: boolean,
    locations: boolean,
    order_id: boolean,
    qty: boolean,
    status: boolean,
    invoiced_at: boolean,
    invoice_number: boolean,
    in_transit_at: boolean,
    delivered_at: boolean,
};

const DEFAULT_FORM_STATE = {
    consider_all_pages: true,
    load_number: true,
    driver_name: true,
    cmr: true,
    legal_entity: true,
    ldg_nr: true,
    client: true,
    brand_model: true,
    transport_vehicle: true,
    locations: true,
    order_id: true,
    qty: true,
    status: true,
    invoiced_at: true,
    invoice_number: true,
    in_transit_at: true,
    delivered_at: true,
};

// @ts-ignore
function SpotLoadExcel(props: IFormProps) {
    const { t, visibility } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [panelState, setPanelState] = useState<boolean>(false);

    // Helper function to get status text
    const getStatusText = (status: string): string => {
        if (!status) return '';
        const statusMap: any = {
            'pending': t('pending'),
            'announced': t('announced'),
            'in-transit': t('in transit'),
            'delivered': t('delivered'),
            'archived': t('archived'),
        };
        return statusMap[status] || status;
    };

    const onSubmit = async (payloads: TInputForm): Promise<void> => {
        const _params: any = props.moduleState;
        const _payloads: any = payloads;

        if ( payloads.consider_all_pages ) {
            delete _params.page;
            delete _params.per_page;
        }

        try {
            const response = await request({
                url: `/suppliers/archived/spot/loads`,
                params: _params,
                paramsSerializer: {
                    serialize: (params: any) => qs.stringify(params)
                },
            });

            delete _payloads.consider_all_pages;
            const columns = pickBy(_payloads, (item: boolean) => ( item ));
            const title: any = getTitles();
            const data: any = [];
            const { dropdowns } = props || {};
            
            map(response.data.data, (value: any) => {
                // Parse driver names - similar to ColumnRenderForDriver
                let driverNameText = '';
                if (value.driver_name) {
                    const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                    const driverData = groupIdsByValue(
                        sortBy(
                            customSplitter(
                                (value.driver_name || "").split("||"),
                                '___',
                                false
                            ),
                            (item: any) => indexOf(idsOrder, item.id)
                        )
                    );
                    driverNameText = driverData
                        .map((i: any) => i.value)
                        .filter((val: any) => !isEmpty(val))
                        .join(', ');
                }

                // Parse legal entity - similar to ColumnRenderForLegalEntity
                let legalEntityText = '';
                if (value.legal_entity) {
                    if (includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad"], value.type)) {
                        const legalEntity = dropdowns?.legalEntities ? find(dropdowns.legalEntities, { id: get(value, ['legal_entity']) }) : null;
                        legalEntityText = get(legalEntity, ['label']) || '';
                    } else {
                        const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                        const legalEntityData = sortBy(
                            customSplitter(
                                (get(value, ['legal_entity']) || "").split("||")
                            ),
                            (item: any) => indexOf(idsOrder, item.id)
                        );
                        const legalEntities = legalEntityData.map((i: any) => {
                            const locations = i.value.split("~~~");
                            const fromLocation = dropdowns?.legalEntities ? find(dropdowns.legalEntities, { id: get(locations, [1]) }) : null;
                            return get(fromLocation, ['label']);
                        }).filter((val: any) => !isEmpty(val));
                        legalEntityText = legalEntities.join(', ');
                    }
                }

                // Parse client - similar to ColumnRenderForClient
                let clientText = '';
                if (value.client_name || value.client) {
                    if (includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad"], value.type)) {
                        const clients = get((value.client_name || "").split("||"), ['0']);
                        const client = get(clients.split("___"), [1]);
                        const clientData = dropdowns?.clients ? find(dropdowns.clients, { id: client }) : null;
                        clientText = get(clientData, ['name']) || '';
                    } else {
                        const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                        const clientData = groupIdsByValue(
                            sortBy(
                                customSplitter(
                                    (value.client_name || "").split("||"),
                                    '___',
                                    false
                                ),
                                (item: any) => indexOf(idsOrder, item.id)
                            )
                        );
                        clientText = clientData
                            .map((i: any) => i.value)
                            .filter((val: any) => !isEmpty(val))
                            .join(', ');
                    }
                }

                // Parse brand model - similar to ColumnRenderForBrandModel
                let brandModelText = '';
                if (value.brand_model) {
                    if (includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad"], value.type)) {
                        const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                        const modelData = groupIdsByValue(
                            sortBy(
                                customSplitter(
                                    (value.brand_model || "").split("||"),
                                    '___',
                                    false
                                ),
                                (item: any) => indexOf(idsOrder, item.id)
                            )
                        );
                        const models = modelData.map((i: any) => {
                            const model = dropdowns?.models ? find(dropdowns.models, { id: i.value }) : null;
                            return get(model, ['label']);
                        }).filter((val: any) => !isEmpty(val));
                        brandModelText = models.join(', ');
                    } else {
                        // For SpotLoad type, use vin_number to get models
                        const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                        const vinData = groupIdsByValue(
                            sortBy(
                                customSplitter(
                                    (value.vin_number || "").split("||"),
                                    '___',
                                    false
                                ),
                                (item: any) => indexOf(idsOrder, item.id)
                            )
                        );
                        const models = vinData.map((i: any) => {
                            const data = find(groupIdsByValue(
                                customSplitter(
                                    (value.brand_model || "").split("||"),
                                    '___',
                                    false
                                )
                            ), (item: any) => includes(item.ids, i.value));
                            const model = dropdowns?.models ? find(dropdowns.models, { id: get(data, ['value']) }) : null;
                            return get(model, ['label']);
                        }).filter((val: any) => !isEmpty(val));
                        brandModelText = models.join(', ');
                    }
                }

                // Parse transport vehicle - similar to ColumnRenderForTransportVehicle
                let transportVehicleText = '';
                if (value.transport_vehicle) {
                    const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                    const vehicleData = groupIdsByValue(
                        sortBy(
                            customSplitter(
                                (value.transport_vehicle || "").split("||"),
                                '___',
                                false
                            ),
                            (item: any) => indexOf(idsOrder, item.id)
                        )
                    );
                    transportVehicleText = vehicleData
                        .map((i: any) => i.value.replaceAll("~~~", " | "))
                        .filter((val: any) => !isEmpty(val))
                        .join(', ');
                }

                // Parse locations - similar to ColumnRenderForLocation
                let locationsText = '';
                if (value.locations) {
                    const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                    const locationData = sortBy(
                        customSplitter(
                            (value.locations || "").split("||")
                        ),
                        (item: any) => indexOf(idsOrder, item.id)
                    );
                    const locationPairs = locationData.map((i: any) => {
                        const locations = i.value.split("~~~");
                        const fromLocation = dropdowns?.locations ? find(dropdowns.locations, { id: get(locations, [0]) }) : null;
                        const toLocation = dropdowns?.locations ? find(dropdowns.locations, { id: get(locations, [1]) }) : null;
                        const fromLabel = fromLocation ? (fromLocation.label || getLocation(fromLocation)) : '';
                        const toLabel = toLocation ? (toLocation.label || getLocation(toLocation)) : '';
                        return fromLabel && toLabel ? `${fromLabel} → ${toLabel}` : (fromLabel || toLabel || '');
                    }).filter((val: any) => !isEmpty(val));
                    locationsText = locationPairs.join(', ');
                }

                // Parse VIN numbers - similar to ColumnRenderForVin
                let vinText = '';
                if (value.vin_number || value.order_id) {
                    const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                    const vinData = groupIdsByValue(
                        sortBy(
                            customSplitter(
                                (value.vin_number || "").split("||"),
                                '___',
                                false
                            ),
                            (item: any) => indexOf(idsOrder, item.id)
                        )
                    );
                    vinText = vinData
                        .map((i: any) => i.value)
                        .filter((val: any) => !isEmpty(val))
                        .join(', ');
                }

                // Parse status - similar to ColumnRenderStatus
                let statusText = '';
                if (value.status) {
                    if (value.type === "SpotLoad" && value.item_status) {
                        const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                        const statusData = groupIdsByValue(
                            sortBy(
                                customSplitter(
                                    (value.item_status || "").split("||"),
                                    '___',
                                    false
                                ),
                                (item: any) => indexOf(idsOrder, item.id)
                            )
                        );
                        const statuses = statusData.map((i: any) => getStatusText(i.value)).filter((val: any) => !isEmpty(val));
                        statusText = statuses.join(', ');
                        if (value.load_invoiced_at) {
                            statusText += (statusText ? ', ' : '') + t('invoiced');
                        }
                    } else {
                        statusText = getStatusText(value.status);
                    }
                }

                // Parse invoice number - similar to ColumnRenderForInvoiceNumber
                let invoiceNumberText = '';
                if (value.invoice_number) {
                    if (includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad"], value.type)) {
                        invoiceNumberText = value.invoice_number || '';
                    } else if (value.type === "SpotLoad") {
                        const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                        const invoiceData = groupIdsByValue(
                            sortBy(
                                customSplitter(
                                    (value.invoice_number || "").split("||"),
                                    '___',
                                    false
                                ),
                                (item: any) => indexOf(idsOrder, item.id)
                            )
                        );
                        invoiceNumberText = invoiceData
                            .map((i: any) => i.value)
                            .filter((val: any) => !isEmpty(val))
                            .join(', ');
                    }
                }

                // Parse invoiced_at - similar to ColumnRenderInvoicedAt
                let invoicedAtText: any = '';
                if (value.invoiced_at) {
                    if (includes(["AlpSpotLoad", "AlpInboundLoad"], value.type)) {
                        invoicedAtText = utcDateToLocalDate(value.invoiced_at);
                    } else {
                        const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                        const dateData = groupIdsByValue(
                            sortBy(
                                customSplitter(
                                    (value.invoiced_at || "").split("||"),
                                    '___',
                                    false
                                ),
                                (item: any) => indexOf(idsOrder, item.id)
                            )
                        );
                        invoicedAtText = dateData
                            .map((i: any) => utcDateToLocalDate(i.value))
                            .filter((val: any) => !isEmpty(val))
                            .join(', ');
                    }
                }

                // Parse in_transit_at - similar to ColumnRenderInTransitAt
                let inTransitAtText: any = '';
                if (value.in_transit_at) {
                    if (includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad"], value.type)) {
                        inTransitAtText = value.actual_loading_time ? utcDateToLocalDate(value.actual_loading_time) : '';
                    } else {
                        const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                        const dateData = sortBy(
                            customSplitter(
                                (value.in_transit_at || "").split("||")
                            ),
                            (item: any) => indexOf(idsOrder, item.id)
                        );
                        inTransitAtText = dateData
                            .map((i: any) => utcDateToLocalDate(i.value))
                            .filter((val: any) => !isEmpty(val))
                            .join(', ');
                    }
                }

                // Parse delivered_at - similar to ColumnRenderDeliveredAt
                let deliveredAtText: any = '';
                if (value.delivered_at) {
                    if (includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad"], value.type)) {
                        deliveredAtText = value.actual_unloading_time ? utcDateToLocalDate(value.actual_unloading_time) : '';
                    } else {
                        const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                        const dateData = sortBy(
                            customSplitter(
                                (value.delivered_at || "").split("||")
                            ),
                            (item: any) => indexOf(idsOrder, item.id)
                        );
                        deliveredAtText = dateData
                            .map((i: any) => utcDateToLocalDate(i.value))
                            .filter((val: any) => !isEmpty(val))
                            .join(', ');
                    }
                }

                // Parse ldg_nr - using customSplitter like in the list
                let ldgNrText = '';
                if (value.ldg_nr) {
                    const ldgNrData = customSplitter(
                        (value.ldg_nr || "").split("||")
                    );
                    ldgNrText = ldgNrData
                        .map((i: any) => i.value)
                        .filter((val: any) => !isEmpty(val))
                        .join(', ');
                }

                // Parse qty - count unique VINs
                let qtyText = '';
                if (value.vin_number) {
                    const idsOrder = value.seq ? uniq(value.seq.split("||")) : [];
                    const qtyData = groupIdsByValue(
                        sortBy(
                            customSplitter(
                                (value.vin_number || "").split("||"),
                                '___',
                                false
                            ),
                            (item: any) => indexOf(idsOrder, item.id)
                        )
                    );
                    qtyText = qtyData.length.toString();
                } else {
                    qtyText = value.qty ? value.qty.toString() : '';
                }

                const row: any = {
                    load_number: value.load_number || '',
                    driver_name: driverNameText,
                    cmr: value.cmr || '',
                    legal_entity: legalEntityText,
                    ldg_nr: ldgNrText,
                    client: clientText,
                    brand_model: brandModelText,
                    transport_vehicle: transportVehicleText,
                    locations: locationsText,
                    order_id: vinText,
                    qty: qtyText,
                    status: statusText,
                    invoiced_at: invoicedAtText,
                    invoice_number: invoiceNumberText,
                    in_transit_at: inTransitAtText,
                    delivered_at: deliveredAtText,
                };

                const _row: any = {};
                map(columns, (_, key: string) => ( _row[key] = row[key] ));
                const arrayOfRow = Object.keys(_row).map((key) => _row[key]);
                data.push(arrayOfRow);
            });

            const _title = map(columns, (_, key: string) => ( title[key] = title[key] ));
            const excel = XLSX.utils.book_new();
            const sheet = XLSX.utils.aoa_to_sheet([_title, ...data]);
            XLSX.utils.book_append_sheet(excel, sheet, t('archived spot loads'));
            XLSX.writeFile(excel, "archived_spot_loads.xlsx");
        } catch (e: any) {
            props.toastify(t("server error"), "error");
        }
    };

    const onClose = () => {
        setPanelState(false);

        if ( props.onClose ) {
            props.onClose();
        }
    };

    const getTitles = () => {
        return {
            load_number: t("load number"),
            driver_name: t("driver"),
            cmr: t("cmr"),
            legal_entity: t("legal entity"),
            ldg_nr: t("ldg nr"),
            client: t('client'),
            brand_model: t('model(s)'),
            transport_vehicle: t('transport vehicle'),
            locations: t('location'),
            order_id: t('vin(s)'),
            qty: t('qty'),
            status: t('status'),
            invoiced_at: t('invoiced at'),
            invoice_number: t('invoice'),
            in_transit_at: t('loading'),
            delivered_at: t('unloading'),
        };
    };

    useEffect(() => {
        if ( visibility ) {
            setPanelState(visibility);
            setInitFormState({ ...DEFAULT_FORM_STATE, ...props.moduleState.filters ?? [] });
        }
    }, [visibility]);

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t("export") } { t('archived spot loads') }</h5>
                            <p>
                                { t("select columns to be exported.") }
                            </p>
                        </div>
                    }
                    visible={ panelState } position="left" onHide={ onClose } blockScroll={ true }
                    style={ { width: '400px' } } closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>
                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                    >
                        { (props: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ props.handleSubmit }>
                                    <Row className={ "mt-4 md:mb-7 sm:mb-7" }>
                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="consider_all_pages"
                                                component={ InputCheckboxField }
                                                label={ t("consider all pages") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="load_number"
                                                component={ InputCheckboxField }
                                                label={ t("load number") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="driver_name"
                                                component={ InputCheckboxField }
                                                label={ t("driver") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="cmr"
                                                component={ InputCheckboxField }
                                                label={ t("cmr") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="legal_entity"
                                                component={ InputCheckboxField }
                                                label={ t("legal entity") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="ldg_nr"
                                                component={ InputCheckboxField }
                                                label={ t("ldg nr") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="client"
                                                component={ InputCheckboxField }
                                                label={ t('client') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="brand_model"
                                                component={ InputCheckboxField }
                                                label={ t('model(s)') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="transport_vehicle"
                                                component={ InputCheckboxField }
                                                label={ t('transport vehicle') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="locations"
                                                component={ InputCheckboxField }
                                                label={ t('location') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="order_id"
                                                component={ InputCheckboxField }
                                                label={ t('vin(s)') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="qty"
                                                component={ InputCheckboxField }
                                                label={ t('qty') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="status"
                                                component={ InputCheckboxField }
                                                label={ t('status') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="invoiced_at"
                                                component={ InputCheckboxField }
                                                label={ t('invoiced at') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="invoice_number"
                                                component={ InputCheckboxField }
                                                label={ t('invoice') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="in_transit_at"
                                                component={ InputCheckboxField }
                                                label={ t('loading') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="delivered_at"
                                                component={ InputCheckboxField }
                                                label={ t('unloading') }
                                            />
                                        </Col>
                                    </Row>
                                    <div className="filter filter-card-footer"
                                         style={ { width: '385px' } }>
                                        <Button type={ "submit" } disabled={ props.isSubmitting }
                                                tooltip={ t("export") }
                                                size={ "small" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-file-excel me-2" />
                                            { t("export") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                onClick={ onClose }
                                                tooltip={ t("operation cancel") } className={ "ms-2" }
                                                size={ "small" } severity={ "danger" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-arrow-left me-2" />
                                            { t("close") }
                                        </Button>
                                    </div>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

SpotLoadExcel.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(SpotLoadExcel);

